/**
 * Two-axis device connectivity — vendored from scio3 `packages/shared-types/src/device-connectivity.ts`
 * (`classifyConnectivity`) + `features/mdm-devices/utils/connectivity.ts`, trimmed to what the drawer
 * renders.
 *
 *  - APP axis (player heartbeat): judged by FRESHNESS — `now − appBeat ≤ 13 min`.
 *  - DEVICE axis (`utilsOnline`, written by mdm-api on agent connect/disconnect): judged by VALUE.
 *
 * Delta vs scio3: staff-api's detail selection has no `utilsLastUpdateDate`, so the 30-day
 * anti-zombie ceiling is applied only when that stamp is present — without it we trust the edge value
 * ("connected", no "as of" time) instead of degrading every agent device to "Unknown".
 */

export const APP_ONLINE_WINDOW_MS = 13 * 60 * 1000;
export const EDGE_STALENESS_CEILING_MS = 30 * 24 * 60 * 60 * 1000;

export const AGENT_PRESENCE = { ONLINE: 1, OFFLINE: 0 } as const;

export type DeviceAxisState = 'online' | 'offline' | 'unknown' | 'unavailable';

export interface DeviceConnectivity {
  appOnline: boolean;
  deviceAxis: DeviceAxisState;
  deviceAsOf: string | null;
  deviceConfidence: 'verified-by-app' | 'edge' | 'none';
  /** App alive but the agent channel reports down — live pulls / utils commands will fail. */
  agentChannelDown: boolean;
}

export interface ConnectivityInput {
  appBeatTs: string | null | undefined;
  utilsOnline: number | null | undefined;
  utilsLastUpdateDate?: string | null;
  /** Live heartbeat status wins over the timestamp window when present. */
  liveAppOnline?: boolean;
  /** Injectable clock (tests). */
  now?: number;
}

const toMs = (v: string | null | undefined): number => {
  if (v == null || v === '') return NaN;
  const t = new Date(v).getTime();
  return Number.isFinite(t) && t > 0 ? t : NaN;
};

export function classifyConnectivity(input: ConnectivityInput): DeviceConnectivity {
  const now = input.now ?? Date.now();
  const appBeat = toMs(input.appBeatTs);
  const appOnline =
    input.liveAppOnline ?? (Number.isFinite(appBeat) && now - appBeat <= APP_ONLINE_WINDOW_MS);

  const hasAgentEvidence =
    input.utilsOnline === AGENT_PRESENCE.ONLINE || input.utilsOnline === AGENT_PRESENCE.OFFLINE;
  const stamp = toMs(input.utilsLastUpdateDate);
  const stampKnown = Number.isFinite(stamp);
  const withinCeiling = !stampKnown || now - stamp <= EDGE_STALENESS_CEILING_MS;
  const asOf = input.utilsLastUpdateDate ?? null;

  if (appOnline) {
    return {
      appOnline: true,
      deviceAxis: hasAgentEvidence ? 'online' : 'unavailable',
      deviceAsOf: hasAgentEvidence ? asOf : null,
      deviceConfidence: hasAgentEvidence ? 'verified-by-app' : 'none',
      agentChannelDown: input.utilsOnline === AGENT_PRESENCE.OFFLINE,
    };
  }
  if (!hasAgentEvidence) {
    return {
      appOnline: false,
      deviceAxis: 'unavailable',
      deviceAsOf: null,
      deviceConfidence: 'none',
      agentChannelDown: false,
    };
  }
  if (input.utilsOnline === AGENT_PRESENCE.OFFLINE) {
    return {
      appOnline: false,
      deviceAxis: 'offline',
      deviceAsOf: asOf,
      deviceConfidence: 'edge',
      agentChannelDown: false,
    };
  }
  if (!withinCeiling) {
    return {
      appOnline: false,
      deviceAxis: 'unknown',
      deviceAsOf: asOf,
      deviceConfidence: 'none',
      agentChannelDown: false,
    };
  }
  // App dead + agent socket connected: the hardware is up, the app isn't.
  return {
    appOnline: false,
    deviceAxis: 'online',
    deviceAsOf: asOf,
    deviceConfidence: 'edge',
    agentChannelDown: false,
  };
}
